import React, { useState } from "react";
import { Heart, Check, MessageCircle, Phone } from "lucide-react";
import { submitPrayer } from "../lib/api";
import { BibleRefLink } from "../lib/bibleRef";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

// Máscara brasileira: (XX) 9XXXX-XXXX
function maskPhoneBR(raw) {
  const d = (raw || "").replace(/\D/g, "").slice(0, 11);
  if (d.length <= 2) return d.length ? `(${d}` : "";
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

export default function Oracao() {
  const [form, setForm] = useState({ name: "", request: "", phone: "" });
  const [phoneError, setPhoneError] = useState("");
  const [loading, setLoading] = useState(false);
  const [pack, setPack] = useState(null);

  const validatePhone = (raw) => {
    const d = (raw || "").replace(/\D/g, "");
    if (d.length < 10) return "Informe DDD + número (mínimo 10 dígitos).";
    if (d.length === 11 && d[2] !== "9") {
      return "Celular com 11 dígitos deve começar com 9 após o DDD.";
    }
    return "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.request.trim()) {
      toast.error("Preencha seu nome e o pedido.");
      return;
    }
    const phoneErr = validatePhone(form.phone);
    if (phoneErr) { setPhoneError(phoneErr); toast.error(phoneErr); return; }
    setLoading(true);
    try {
      const res = await submitPrayer({
        name: form.name,
        request: form.request,
        phone: form.phone,
        contact: form.phone, // compat com schema legado
      });
      setPack(res.pack || null);
      toast.success("Recebemos seu pedido de oração.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível enviar agora. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const update = (k) => (e) => {
    if (k === "phone") {
      setPhoneError("");
      setForm({ ...form, phone: maskPhoneBR(e.target.value) });
    } else {
      setForm({ ...form, [k]: e.target.value });
    }
  };

  return (
    <div className="max-w-5xl mx-auto section-padding py-8" data-testid="prayer-page">
      <div className="max-w-2xl mx-auto">
        <div className="text-center">
          <div className="inline-flex w-14 h-14 rounded-full bg-gold/10 items-center justify-center">
            <Heart className="w-6 h-6 text-gold" />
          </div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-navy/50 mt-6">Estamos com você em oração</p>
          <h1 className="font-display text-4xl sm:text-5xl text-navy mt-2">Pedido de Oração</h1>
          <p className="text-navy/70 mt-4">
            Registre aqui o que pesa em seu coração. O seu pedido será recebido com respeito
            e apresentado ao Pai em oração.
          </p>
        </div>

        {pack ? (
          <div
            className="mt-10 bg-white border border-border rounded-2xl p-8 fade-in text-center"
            data-testid="prayer-success"
          >
            <div className="inline-flex w-12 h-12 rounded-full bg-gold/20 items-center justify-center">
              <Check className="w-6 h-6 text-gold" />
            </div>
            <h2 className="font-display text-2xl text-navy mt-5">Pedido recebido</h2>
            <p className="text-navy/70 mt-3">
              Que a paz do Senhor guarde o seu coração. Estaremos intercedendo por você.
            </p>

            {/* Pastoral verse pack */}
            <div className="mt-8 bg-[hsl(var(--parchment))] rounded-2xl p-6 text-left border border-gold/20">
              <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Uma palavra para você</p>
              <blockquote
                className="font-display text-xl text-navy mt-3 leading-snug"
                data-testid="prayer-verse-text"
              >
                “{pack.verse_text}”
              </blockquote>
              <BibleRefLink
                refText={pack.verse_ref}
                testid="prayer-verse-ref"
                className="mt-3 inline-block text-sm text-gold tracking-wide hover:underline hover:text-navy transition-colors"
              />
              {pack.note && (
                <p className="mt-5 text-sm text-navy/80 italic border-t border-gold/20 pt-4">
                  {pack.note}
                </p>
              )}
            </div>

            {/* WhatsApp CTA */}
            {pack.whatsapp_url && (
              <a
                href={pack.whatsapp_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="prayer-whatsapp-cta"
                className="mt-6 inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#1ebe57] text-white font-medium px-6 py-3.5 rounded-full transition-colors"
              >
                <MessageCircle className="w-5 h-5" />
                <span>Continuar no WhatsApp</span>
              </a>
            )}

            <button
              onClick={() => { setPack(null); setForm({ name: "", request: "", phone: "" }); }}
              className="block mx-auto mt-6 text-xs uppercase tracking-widest text-navy/50 hover:text-navy"
              data-testid="prayer-new-request"
            >
              Enviar outro pedido
            </button>
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="mt-10 bg-white border border-border rounded-2xl p-6 sm:p-8 space-y-5"
            data-testid="prayer-form"
          >
            <div>
              <label className="text-xs uppercase tracking-widest text-navy/60">Seu nome</label>
              <Input
                className="mt-2 h-12 bg-[hsl(var(--parchment))] border-transparent focus:bg-white"
                placeholder="Como deseja ser chamado(a)?"
                value={form.name}
                onChange={update("name")}
                data-testid="prayer-input-name"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-navy/60">Seu pedido</label>
              <Textarea
                className="mt-2 min-h-[140px] bg-[hsl(var(--parchment))] border-transparent focus:bg-white"
                placeholder="Descreva o que deseja entregar em oração."
                value={form.request}
                onChange={update("request")}
                data-testid="prayer-input-request"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-navy/60">
                WhatsApp <span className="text-rose-600">*</span>
              </label>
              <div className="relative mt-2">
                <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-navy/40 pointer-events-none" />
                <Input
                  className={`pl-10 h-12 bg-[hsl(var(--parchment))] focus:bg-white ${phoneError ? "border-rose-500" : "border-transparent"}`}
                  placeholder="(81) 99999-9999"
                  value={form.phone}
                  onChange={update("phone")}
                  inputMode="tel"
                  data-testid="prayer-input-phone"
                  required
                />
              </div>
              {phoneError ? (
                <p className="text-[11px] text-rose-600 mt-1.5">{phoneError}</p>
              ) : (
                <p className="text-[11px] text-navy/50 mt-1.5">
                  A equipe pastoral entrará em contato pelo seu WhatsApp para orar com você.
                </p>
              )}
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="prayer-submit"
              className="w-full h-12 rounded-full bg-navy text-white hover:bg-gold hover:text-navy transition-colors uppercase tracking-widest text-xs"
            >
              {loading ? "Enviando..." : "Enviar pedido"}
            </Button>
            <p className="text-center text-xs text-navy/50">
              Seu pedido é recebido com discrição e cuidado.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
